/**
 * Created by Alex on 05/29/2023.
 * Description:
 * Adminnotifications Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { of, Subject, Subscription } from 'rxjs';
import { AppState } from '../../../../redux';
import { catchError, filter, take } from 'rxjs/operators';
import { DEFAULT_PAGE_SIZE } from '../../grid-utility';
import { ResearchView } from '../../../../constants/business.constants';
import { NotificationActionTypes } from '../../../../redux/actions/notification.actions';
import { notificationSelector } from '../../../../redux/reducers/notification.reducer';
import { DateHelperService } from '../../../../tamalelibs/services/date-helper.service';
import { NotificationModel } from '../../../../tamalelibs/models/notification.model';
import { AdminNotificationActionsComponent } from './admin-notification-actions.componment';
import { AdminNotificationActiveComponent } from './admin-notification-active.component';
import { AdminNotificationActionService, NotificationActionType } from './admin-notification.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { SystemActionTypes } from '../../../../tamalelibs/redux/actions/system.actions';
import { ButtonConfig, SplitButtonConfig } from '../../../../widgets/split-button/split-button.model';
import { NotificationService } from '../../../../tamalelibs/services/notification.service';

@Component({
    selector: 'tam-admin-notifications',
    templateUrl: './admin-notifications.component.html',
    styleUrls: ['./admin-notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminNotificationsComponent implements OnInit, OnDestroy {
    /**
    * an array of column definitions.
    */
    columnDefs: Array<ColDef> = [];

    /**
     * The option settings for the inner grid.
     */
    gridOptions: GridOptions;

    /**
     * for spinner
     */
    loading = true;

    /**
     * new notification button
     */
    newNotification = StringLiteralsPipe.translate('notification.new_notification_rule');

    /**
     * default to hide the no data indicator.
     */
    noDataStyle = 'none';

    /**
     * the data source of the grid.
     */
    rowData: Array<NotificationModel> = [];
    splitButtonConfig: SplitButtonConfig;

    /**
     * the Grid API reference
     */
    private _gridApi: GridApi;
    private _columnApi;

    /**
     * the subscription collection persisting for unsubscribing them on destroying
     */
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _store: Store<AppState>,
        private _dateHelperService: DateHelperService,
        private _adminNotificationActionService: AdminNotificationActionService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _slideSheetService: SlideSheetService,
        private _toast: ToastService,
        private _notificationService: NotificationService) { }

    ngOnInit() {
        this._initSplitButton();
        this._store.dispatch({
            type: NotificationActionTypes.GETNOTIFICATIONS
        });
        // to be used by edit notification page
        this._store.dispatch({
            type: SystemActionTypes.GET_CONTACTS
        });
        this._destroySubscriptions.push(
            this._store.pipe(
                select(notificationSelector),
                filter(res => res),
            ).subscribe(res => {
                if (res.type === NotificationActionTypes.GETNOTIFICATIONS_SUCCESS) {
                    this.rowData = res.payload;
                    // Due to when switch router,grid header was loss.reload grid config can fix this issue.
                    this._loadGridConfig();
                    this._changeDetectorRef.detectChanges();
                }
                this.loading = false;
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
            this._adminNotificationActionService.subject$.subscribe((action) => {
                if (action.type === NotificationActionType.DELETE_NOTIFICATION) {
                    const expandedNotes = this._getAllGroupExpandedNodes();
                    this._removeItem(action.payload.id);
                    this._setAllGroupExpandedNodes(expandedNotes);
                    this._changeDetectorRef.detectChanges();
                }
            }),
        );
        this._loadGridConfig();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    /** Triggered when create button is clicked; opens editor menu */
    onButtonClick() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_NOTIFICATION_EDITOR
        });

    }

    onConvertRichclientNotification() {
        this._notificationService.convertRichclientNotifications().pipe(
            catchError((error) => {
                this._toast.notify({
                    message: error.message,
                    style: NotificationStyles.Error
                });
                return of(null);
            }),
            take(1)
        ).subscribe(response => {
            if (response === null) {
                this._toast.notify({
                    style: NotificationStyles.Warning,
                    message: 'No data was found that needed to be converted.'
                });
            } else {
                const summary = response['summary'].toString();
                const failedCases = summary.split(' ')[6];
                const allCases = summary.split(' ')[2];
                const successedCases = response['succed'];
                // add converted cases to grid
                const notificationsConverted = successedCases.map(element => NotificationModel.parse(element));
                this.rowData.push(...notificationsConverted);
                if (this._gridApi) {
                    this._gridApi.setRowData(this.rowData);
                    this._gridApi.sizeColumnsToFit();
                }
                // show notiificaiton by the different cases
                if (failedCases === '0') {
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: 'All settings are successfully converted.'
                    });
                } else if (failedCases === allCases) {
                    this._toast.notify({
                        style: NotificationStyles.Error,
                        message: 'Conversion failed, please check log and Richclient.'
                    });
                } else {
                    this._toast.notify({
                        style: NotificationStyles.Warning,
                        message: failedCases + ' settings were failed to convert, please check log and Richclient.'
                    });
                }
            }
        });
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onColumnEverythingChanged(event) {
        if (event.source === 'contextMenu') {
            const sort = [
                {
                    colId: 'name',
                    sort: 'asc'
                }
            ];
            this._columnApi.setRowGroupColumns([]);
            this._gridApi.setFilterModel(null);
            this._columnApi.applyColumnState({ state: sort });
            this._changeDetectorRef.detectChanges();
        }
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;
    }

    private _compareValue(columnDefs) {
        columnDefs.forEach(def => {
            def.comparator = function (valueA, valueB, nodeA, nodeB) {
                if (valueA && valueB && valueA.length > 0 && valueB.length > 0) {
                    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
                }
            };
        });
    }

    private _generateColDefs(): Array<ColDef> {
        const cellStyleHandler = () => {
            return {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none'
            };
        };
        return [
            {
                headerName: 'Name',
                colId: 'name',
                valueGetter: 'data.name',
                tooltipValueGetter: this._tooltip,
                sortable: true,
                minWidth: 50,
                width: 150,
                sort: 'asc',
                enableRowGroup: true,
                cellStyle: function (params) {
                    if (params.data && !params.data.active) {
                        return {
                            opacity: 0.45,
                        };
                    }
                }
            },
            {
                headerName: 'Description',
                colId: 'description',
                valueGetter: 'data.description',
                tooltipValueGetter: this._tooltip,
                sortable: true,
                minWidth: 50,
                width: 230,
                enableRowGroup: true,
                cellStyle: function (params) {
                    if (params.data && !params.data.active) {
                        return {
                            opacity: 0.45,
                        };
                    }
                }
            },
            {
                headerName: 'Created by',
                colId: 'creator',
                valueGetter: 'data.creator',
                tooltipValueGetter: this._tooltip,
                minWidth: 50,
                maxWidth: 200,
                sortable: true,
                enableRowGroup: true,
                cellStyle: function (params) {
                    if (params.data && !params.data.active) {
                        return {
                            opacity: 0.45,
                        };
                    }
                }
            },
            {
                headerName: 'Created date',
                colId: 'creatTime',
                valueGetter: (params) => {
                    if (params.data) {
                        const date = new Date(params.data.creatTime);
                        return this._dateHelperService.getSpecificFormatOfDate(date, true);
                    }
                },
                tooltipValueGetter: this._tooltip,
                minWidth: 50,
                maxWidth: 200,
                sortable: true,
                enableRowGroup: true,
                cellStyle: function (params) {
                    if (params.data && !params.data.active) {
                        return {
                            opacity: 0.45,
                        };
                    }
                }
            },
            {
                headerName: 'Last modified date',
                colId: 'modifyTime',
                valueGetter: (params) => {
                    if (params.data) {
                        const date = new Date(params.data.modifyTime);
                        return this._dateHelperService.getSpecificFormatOfDate(date, true);
                    }
                },
                tooltipValueGetter: this._tooltip,
                minWidth: 50,
                maxWidth: 200,
                sortable: true,
                enableRowGroup: true,
                cellStyle: function (params) {
                    if (params.data && !params.data.active) {
                        return {
                            opacity: 0.45,
                        };
                    }
                }
            },
            {
                headerName: '',
                headerComponentParams: {
                    template:
                        '<div class="ag-cell-label-container" role="presentation" style="width:100%;">' +
                        '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
                        '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="width:100%;display:flex;">' +
                        '    <span ref="eText" class="ag-header-cell-text" role="columnheader" style="flex:1;text-align:center;"></span>' +
                        '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
                        '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
                        '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
                        '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
                        '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
                        '  </div>' +
                        '</div>'
                },
                valueGetter: 'data.active',
                colId: 'active',
                cellStyle: () => {
                    return Object.assign({}, cellStyleHandler(), {
                        'padding': '10px 0 0 0',
                        'margin': 0,
                        'height': '100%',
                        'text-align': 'center'
                    });
                },
                minWidth: 120,
                width: 120,
                maxWidth: 120,
                sortable: false,
                suppressColumnsToolPanel: true,
                menuTabs: [],
                cellRenderer: AdminNotificationActiveComponent,
            },
            {
                cellStyle: (params) => {
                    return Object.assign({}, cellStyleHandler(), {
                        'padding': '12px 0 0 0',
                        'margin': 0,
                        'height': '100%',
                        'text-align': 'center'
                    });
                },
                headerName: '',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                sortable: false,
                suppressColumnsToolPanel: true,
                suppressMovable: true,
                menuTabs: [],
                cellRenderer: AdminNotificationActionsComponent,
            }
        ];
    }

    private _generateGridOpions(): GridOptions {
        return {
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: 'agSetColumnFilter',
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            cacheBlockSize: ResearchView.cacheBlockSize,
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            groupDisplayType: 'groupRows',
            paginationPageSize: DEFAULT_PAGE_SIZE,
            colResizeDefault: 'shift',
            rowSelection: 'single', // we only need to support single selection on notification list.
            rowGroupPanelShow: 'always',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            suppressRowTransform: true,
            getRowHeight: () => 40,
        };
    }

    /**
     * we need to store the expanded notes since we need to restore it after deleting a row
     * @returns
     */
    private _getAllGroupExpandedNodes(): string[] {
        const expandedGroupNodes = [];
        const rowCount = this._gridApi.getDisplayedRowCount();
        for (let i = 0; i < rowCount; i++) {
            const rowNode = this._gridApi.getDisplayedRowAtIndex(i);
            if (rowNode && rowNode.group && rowNode.expanded) {
                expandedGroupNodes.push(rowNode.key);
            }
        }
        return expandedGroupNodes;
    }

    private _initSplitButton() {
        this.splitButtonConfig = {
            primaryAction: {
                displayText: this.newNotification,
                onClick: () => {
                    this.onButtonClick();
                }
            } as ButtonConfig,
            secondaryActions: [
                {
                    displayText: StringLiteralsPipe.translate('notification.convert_richclient_notification_settings'),
                    onClick: () => {
                        this.onConvertRichclientNotification();
                    }
                }
            ],
            feedbackSubject$: new Subject<any>()
        } as SplitButtonConfig;
    }

    private _loadGridConfig() {
        this.columnDefs = this._generateColDefs();
        this._compareValue(this.columnDefs);
        this.gridOptions = this._generateGridOpions();
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === NotificationActionTypes.CREATENOTIFICATIONS_SUCCESS) {
            this._store.dispatch({
                type: NotificationActionTypes.GETNOTIFICATIONS
            });
            this._toast.notify({
                style: NotificationStyles.Success,
                message: action.payload.successMessage
            });
        } if (action.type === NotificationActionTypes.EDITNOTIFICATIONS_SUCCESS) {
            this._store.dispatch({
                type: NotificationActionTypes.GETNOTIFICATIONS
            });
            this._toast.notify({
                style: NotificationStyles.Success,
                message: action.payload.successMessage
            });
        }
    }

    private _removeItem(id: string) {
        const index = this.rowData.indexOf(this.rowData.find(data => (data.id === id)));
        this.rowData.splice(index, 1);

        if (this._gridApi) {
            this._gridApi.setRowData(this.rowData);
            this._gridApi.sizeColumnsToFit();
        }
    }

    /**
     * set the expanded status back
     * @param expandedNotes
     */
    private _setAllGroupExpandedNodes(expandedNotes: string[]) {
        this._gridApi.forEachNode(node => {
            if (node.group && expandedNotes.includes(node.key)) {
                node.setExpanded(true);
            }
        });
    }

    private _tooltip(params) {
        return (params.value);
    }
}
